import React, { useEffect, useMemo, useState } from "react";
import {
  FaUtensils,
  FaBus,
  FaWifi,
  FaGamepad,
  FaMoneyBillAlt,
  FaPen,
} from "react-icons/fa";
import AddBudgetModal from "./AddBudgetModal";
import EditBudgetModal from "./EditBudgetModal";
import HistoryBudgetsTable from "./HistoryBudgetsTable";
import "./anggaran.css";
const REACT_APP_EXPRESS_API = process.env.REACT_APP_EXPRESS_API;

const API_BASE = `${REACT_APP_EXPRESS_API}/api/budget`;

// Label, ikon, dan warna kategori
const LABELS = {
  makanan_kebutuhan_rumah_tangga: "Makanan & Kebutuhan Rumah Tangga",
  transportasi: "Transportasi",
  konektivitas: "Konektivitas",
  hiburan: "Hiburan",
  tagihan_lainnya: "Tagihan & Lainnya",
};

const ICONS = {
  makanan_kebutuhan_rumah_tangga: <FaUtensils />,
  transportasi: <FaBus />,
  konektivitas: <FaWifi />,
  hiburan: <FaGamepad />,
  tagihan_lainnya: <FaMoneyBillAlt />,
};

const COLORS = {
  makanan_kebutuhan_rumah_tangga: "#1E3A8A",
  transportasi: "#F7931E",
  konektivitas: "#1E3A8A",
  hiburan: "#F7931E",
  tagihan_lainnya: "#1E3A8A",
};

// Helper konversi ke Rupiah
const toIDR = (n) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(Number(n || 0));

const toInputDate = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
};

const calculateEndDate = (startDateString) => {
  if (!startDateString) return "";
  const startDate = new Date(startDateString);
  if (Number.isNaN(startDate.getTime())) return "";
  const endDate = new Date(startDate);
  endDate.setDate(startDate.getDate() + 30);
  return toInputDate(endDate);
};

export default function Anggaran() {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [raw, setRaw] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [formData, setFormData] = useState({
    startDate: "",
    endDate: "",
    makanan: "",
    transportasi: "",
    konektivitas: "",
    hiburan: "",
    tagihan: "",
  });

  // 🟢 Fetch anggaran aktif
  const fetchBudgets = async () => {
    try {
      setLoading(true);
      setErr("");
      const res = await fetch(`${API_BASE}/active`, {
        method: "GET",
        credentials: "include",
      });
      if (!res.ok) throw new Error(`API Error ${res.status}`);
      const json = await res.json();
      setRaw(json);

      // Update tanggal periode aktif
      const budgeting = json?.budgeting;
      if (budgeting) {
        setStartDate(toInputDate(budgeting.startDate));
        setEndDate(toInputDate(budgeting.endDate));
      }
    } catch (e) {
      console.error("Fetch error:", e);
      setErr(e.message || "Gagal memuat data anggaran");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBudgets();
  }, []);

  // Ambil kategori aktif
  const categoriesObj = useMemo(() => {
    return (
      raw?.budgeting?.categories_1 ??
      raw?.budgeting?.categories ??
      raw?.data?.budgeting?.categories ??
      raw?.categories ??
      {}
    );
  }, [raw]);

  const categories = useMemo(() => {
    return Object.keys(categoriesObj).map((key) => ({
      key,
      name: LABELS[key] || key,
      amount: toIDR(categoriesObj[key]),
      color: COLORS[key] || "#1E3A8A",
      icon: ICONS[key] || <FaMoneyBillAlt />,
    }));
  }, [categoriesObj]);

  // 🟡 Input handler
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const numericValue = value.replace(/[^0-9]/g, "");
    setFormData((prev) => ({ ...prev, [name]: numericValue }));
  };

  const handleStartDateChange = (e) => {
    const newStartDate = e.target.value;
    const newEndDate = calculateEndDate(newStartDate);
    setFormData((prev) => ({
      ...prev,
      startDate: newStartDate,
      endDate: newEndDate,
    }));
  };

  // 🟢 Tambah anggaran baru
  const handleSave = async () => {
    try {
      if (!formData.startDate || !formData.endDate) {
        alert("Tanggal mulai dan berakhir harus diisi!");
        return;
      }
      const periodDate = new Date(formData.startDate);
      const period_name = periodDate.toLocaleDateString("id-ID", {
        month: "long",
        year: "numeric",
      });

      const res = await fetch(API_BASE, {
        headers: {
          "Content-Type": "application/json", // ✅ Missing before
        },
        method: "POST",
        credentials: "include",
        body: JSON.stringify({
          period_name,
          "startDate": formData.startDate,
          "endDate": formData.endDate,
          "categories_1": {
            "makanan_kebutuhan_rumah_tangga": Number(formData.makanan || 0),
            "transportasi": Number(formData.transportasi || 0),
            "konektivitas": Number(formData.konektivitas || 0),
            "hiburan": Number(formData.hiburan || 0),
            "tagihan_lainnya": Number(formData.tagihan || 0),
          },
        }),
      });

      if (!res.ok) throw new Error("Gagal menambah anggaran");

      alert("✅ Anggaran baru berhasil ditambahkan!");
      setShowAddModal(false);
      setFormData({
        startDate: "",
        endDate: "",
        makanan: "",
        transportasi: "",
        konektivitas: "",
        hiburan: "",
        tagihan: "",
      });

      await fetchBudgets();
    } catch (e) {
      console.error("Error Add:", e);
      alert("Gagal menambah data anggaran: " + e.message);
    }
  };

  const handleAddBudgetClick = () => {
    const today = toInputDate(new Date());
    setFormData({
      startDate: today,
      endDate: calculateEndDate(today),
      makanan: "",
      transportasi: "",
      konektivitas: "",
      hiburan: "",
      tagihan: "",
    });
    setShowAddModal(true);
  };

  // 🟠 Edit anggaran aktif
  const handleEditBudget = () => {
    if (!raw?.budgeting) {
      alert("Tidak ada anggaran aktif untuk diedit!");
      return;
    }

    const categories = raw.budgeting.categories_1 || {};
    setFormData({
      startDate: toInputDate(raw.budgeting.startDate),
      endDate: toInputDate(raw.budgeting.endDate),
      makanan: categories.makanan_kebutuhan_rumah_tangga || "",
      transportasi: categories.transportasi || "",
      konektivitas: categories.konektivitas || "",
      hiburan: categories.hiburan || "",
      tagihan: categories.tagihan_lainnya || "",
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      if (!formData.startDate) {
        alert("Tanggal mulai harus diisi!");
        return;
      }

      setIsSaving(true);
      const id = raw?.budgeting?._id;

      if (!id) {
        alert("ID anggaran tidak ditemukan!");
        return;
      }
      const payload = {
        "startDate": formData.startDate,
        "endDate": formData.endDate,
        "categories_1": {
          "makanan_kebutuhan_rumah_tangga": Number(formData.makanan || 0),
          "transportasi": Number(formData.transportasi || 0),
          "konektivitas": Number(formData.konektivitas || 0),
          "hiburan": Number(formData.hiburan || 0),
          "tagihan_lainnya": Number(formData.tagihan || 0),
        },
      };

      const res = await fetch(`${API_BASE}/${id}`, {
        headers: {
          "Content-Type": "application/json", // ✅ Missing before
        },
        method: "PUT",
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Gagal memperbarui anggaran");

      alert("✅ Anggaran berhasil diperbarui!");
      setShowEditModal(false);
      await fetchBudgets();
    } catch (e) {
      console.error("Error Edit:", e);
      alert(`❌ Gagal memperbarui anggaran: ${e.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="anggaran-container">
      <div className="breadcrumb-agr">
        <span className="breadcrumb-item">Halaman</span>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-item active">Anggaran</span>
      </div>
      <h2>Anggaran</h2>

      <p className="desc">
        Catat, pantau, dan kendalikan setiap pengeluaran secara terstruktur sesuai dengan prioritas. <br />
        Wujudkan keuangan yang sehat melalui manajemen anggaran yang transparan dan akurat.
      </p>

      <div className="date-selector">
        <div className="date-field">
          <label>Anggaran Aktif</label>
          <div style={{ marginTop: 4, fontSize: 12, color: "#6b7280" }}>
            Periode:{" "}
            <b style={{ color: "#111827" }}>
              {startDate && endDate
                ? `${new Date(startDate).toLocaleDateString("id-ID")} – ${new Date(endDate).toLocaleDateString("id-ID")}`
                : "-"}
            </b>
          </div>
        </div>

        <div className="button-group">
          <button className="btn add-btn-agr" onClick={handleAddBudgetClick}>
            + Add
          </button>
          <button
            className="btn edit-btn-agr"
            onClick={handleEditBudget}
            disabled={!raw?.budgeting}
          >
            <FaPen /> Edit
          </button>
        </div>
      </div>

      {loading && <div className="status">Memuat data anggaran…</div>}
      {err && <div className="status error">{err}</div>}

      {!loading && !err && Object.keys(categoriesObj).length > 0 && (
        <div className="budget-list">
          {categories.map((item) => (
            <div
              key={item.key}
              className="budget-card"
              style={{ borderColor: item.color }}
            >
              <div className="left-section" style={{ color: item.color }}>
                <div className="icon-wrapper">{item.icon}</div>
                <div className="category-name">{item.name}</div>
              </div>
              <div
                className="right-section"
                style={{ backgroundColor: item.color }}
              >
                <span className="amount">{item.amount}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Add */}
      {showAddModal && (
        <AddBudgetModal
          formData={formData}
          handleStartDateChange={handleStartDateChange}
          handleInputChange={handleInputChange}
          handleSave={handleSave}
          onClose={() => setShowAddModal(false)}
          isSaving={isSaving}
        />
      )}

      {/* Modal Edit */}
      {showEditModal && (
        <EditBudgetModal
          formData={formData}
          setFormData={setFormData}
          handleStartDateChange={handleStartDateChange}
          handleInputChange={handleInputChange}
          handleSaveEdit={handleSaveEdit}
          onClose={() => setShowEditModal(false)}
        />
      )}

      {/* History */}
      <HistoryBudgetsTable />
    </div>
  );
}